import Avatar from "@components/ui-components/avatar";
import { Badge } from "reactstrap";
import Flex from "@components/ui-components/flex";
import { Link, useParams } from "react-router-dom";
import LoadingButton from "@components/ui-components/loading-button";
import LongNameFormatter from "@components/formatters/long-name-formatter";
import { getStatusBadge } from "@src/utility/helpers/utils";
import size from "lodash/size";
import useDateHandler from "@hooks/use-date-handler";
import HeatMap from "./heat-map";

const decoColumns = ({ handleRowActions, kpi = [] }) => {
    const { dateFormatter } = useDateHandler();
    const { platformId } = useParams();

    const baseColumns = [
        {
            name    : "Name",
            sort_by : "company_name",
            selector: (row) => row?.company_name ?? "",
            sortable: true,
            width   : "300px",
            cell    : (row) => (
                <Flex align="center">
                    <Avatar
                        color={getStatusBadge(row?.status).color}
                        img={false}
                        content={row?.company_name}
                        initials/>
                    <div>
                        <LongNameFormatter
                            tag={Link}
                            to={`/product/${platformId}/companies/${row._id}`}
                            content={row.company_name}
                            className="fw-bold"
                            id={`name-${row._id}`}
                            max={35}/>
                        <Badge color={getStatusBadge(row.status).color}>
                            {getStatusBadge(row.status).title}
                        </Badge>
                    </div>
                </Flex>
            )
        },
        {
            name    : "Account Management Details",
            selector: (row) => row.sdr,
            width   : "200px",
            cell    : (row) => {
                return (
                    <Flex column>
                        <p>
                            <b>SDR:</b> {row.sdr.name}
                        </p>
                        <p>
                            <b>CSM:</b> {row.csm.name}
                        </p>
                    </Flex>
                );
            }
        },
        {
            name    : "Activity HeatMap",
            selector: (row) => row.heatMap,
            width   : "270px",
            cell    : (row) => <HeatMap heatMapData={row.heatMap || []} />
        },
        {
            name    : "Stage",
            sort_by : "stage",
            selector: (row) => row?.stage?.name ?? "",
            sortable: true,
            width   : "200px",
            cell    : (row) => row.stage.name || ""
        },
        {
            name    : "Tags",
            selector: (row) => row?.tags ?? 0,
            width   : "200px",
            cell    : (row) => (
                <Flex wrap>
                    {size(row.tags) ? row.tags.map((item) => (
                        <Badge color="primary" className="mb-25">
                            {item.name}
                        </Badge>
                    )) : "-"}
                </Flex>
            )
        },
        {
            name    : "People",
            selector: (row) => row?.people_count ?? 0,
            width   : "150px"
        },
        {
            name    : "MRR",
            sortable: true,
            sort_by : "mrr",
            selector: (row) => row?.mrr ?? 0,
            format  : (row) => `$${row?.mrr ?? 0}`,
            width   : "150px"
        },
        {
            name    : "Created On",
            sort_by : "date",
            selector: (row) => row?.date ?? "",
            sortable: true,
            width   : "250px",
            cell    : (row) => (
                <span>
                    <i className="mdi mdi-calendar me-1 font-medium-2" />
                    {dateFormatter({
                        date: row?.date
                    })}
                </span>
            )
        }
    ];

    const isValidDate = (value) => {
        if (!value) return false;
        if (typeof value !== "string") return false;

        const normalized =
      typeof value === "string" ? value.replace(" ", "T") : value;

        const parsed = new Date(normalized);
        return !isNaN(parsed.getTime());
    };

    const getDisplayValue = (itemName, val, row, dateFormatter) => {
        if (isValidDate(val)) {
            return dateFormatter({ date: val });
        }
        if (typeof val === "boolean") {
            return val ? "Yes" : "No";
        }
        if (
            typeof val === "object" &&
      ["platform", "plan", "p_comp"].includes(itemName)
        ) {
            return row?.[itemName]?.name || "-";
        }
        if (val === null || val === undefined || val === "") {
            return "-";
        }
        return String(val);
    };

    const specialNames = {
        cid      : "Internal Id",
        is_p_comp: "Is Parent Company",
        p_comp   : "Parent Company"
    };
    const formatName = (name) => {
        if (specialNames[name]) return specialNames[name];

        return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    };
    const getValue = (row, key) => row?.custom_attributes?.[key] ?? row?.[key];
    const dynamicColumns = (kpi || [])
        .filter(({ value, status }) => value === true && status === "active")
        .map((item) => {
            const { name } = item;

            return {
                name    : formatName(name),
                width   : "200px",
                selector: (row) => getValue(row, name),
                cell    : (row) => {
                    const val = getValue(row, name);
                    return getDisplayValue(name, val, row, dateFormatter);
                }
            };
        });

    const actionColumn = {
        name: "Actions",
        cell: (row) => (
            <>
                <LoadingButton
                    className="me-1"
                    outline
                    color="info"
                    subject="companies"
                    action="update"
                    id={`${row._id}-edit-button`}
                    icon="pencil"
                    onClick={() => handleRowActions(row, "edit")}/>
                <LoadingButton
                    outline
                    color="danger"
                    subject="companies"
                    action="delete"
                    id={`${row._id}-edit-delete`}
                    icon="delete"
                    onClick={() => handleRowActions(row, "delete")}/>
            </>
        )
    };

    return [...baseColumns, ...dynamicColumns, actionColumn];
};

export default decoColumns;
